// German UI strings — the source of truth. en.ts must provide the same shape
// (typed as Messages), so a key missing in English is a compile error.
// Group by component; functions take already-formatted values.
//
// Only UI chrome lives here. Data values (expenditure types, Einzelplan
// names) are translated for display in labels.ts; title-level texts
// (Zweckbestimmung, Kapitel, Funktion, Gruppierung) stay German everywhere.
export const de = {
  common: {
    close: 'Schließen',
    back: 'Zurück',
    done: 'Fertig',
    reset: 'Zurücksetzen',
    search: 'Suchen...',
    removeFilter: 'Filter entfernen',
    noData: 'Keine Daten',
    ist: 'Ist',
    soll: 'Soll',
    year: (year: number) => `Jahr: ${year}`,
    /** "S. 123" — page reference into a printed budget document. */
    page: (page: number | string | null) => `S. ${page ?? ''}`,
    // Matches chartExport.ts and the embed footer: one credit line everywhere.
    source: 'Quelle: Bundeshaushalt, dezernatzukunft.org/daten/haushaltstracker',
  },

  units: {
    absolute: 'Mrd. €',
    gdpPercent: '% des BIP',
    budgetPercent: '% des Haushalts',
  },

  widget: {
    loading: 'Lade Haushaltsdaten...',
    loadError: (error: string) => `Fehler beim Laden der Daten: ${error}`,
    errorBanner: (parts: string) =>
      `Einige Daten konnten nicht geladen werden: ${parts}. Die Anzeige ist eingeschränkt.`,
    errorPartGdp: 'BIP-Daten',
    errorPartTotals: 'Haushaltssummen',
    errorPartFilterOptions: 'Filteroptionen',
    retry: 'Erneut versuchen',
    dismissNotice: 'Hinweis schließen',
    toastGdpUnavailable: 'BIP-Daten nicht verfügbar — wechsle zu absoluten Werten',
    toastTotalsUnavailable: 'Haushaltssummen nicht verfügbar — wechsle zu absoluten Werten',
    typeToggleLabel: 'Einnahmen oder Ausgaben',
    revenue: 'Einnahmen',
    expenditure: 'Ausgaben',
    unit: 'Einheit',
    rootRevenue: 'Gesamteinnahmen des Bundes',
    rootExpenditure: 'Gesamtausgaben des Bundes',
    firstPublishedDate: '12. Juni 2026',
    footerDates: (lastUpdated: string) =>
      `Erstveröffentlichung: 12. Juni 2026 | Letztes Update: ${lastUpdated}`,
    methodology:
      'Die Übersicht und der Datensatz zu titelscharfen Bundesausgaben basiert auf einer automatisierten Erfassung der Ist-Werte für alle Ausgabentitel aus den Haushaltsrechnungen zum Bundeshaushalt und der Sondervermögen (sofern vorhanden) seit 2016. Für Jahre, für die noch keine Ist-Daten vorliegen, werden Soll-Werte verwendet; im Fall von Nachtragshaushalten wird der jeweils letzte Soll-Wert angezeigt. Um nur Ausgaben zu erfassen, die tatsächliche Geldflüsse in die Wirtschaft darstellen, werden die Daten um Zuführungen an Rücklagen sowie Finanzflüsse zwischen Bundeshaushalt und Sondervermögen bereinigt. Die Zuordnung zu Subventionen und öffentlichen Leistungen erfolgt je nach Empfänger: Zahlungen, die direkt an Haushalte und Unternehmen fließen, werden als Subventionen gezählt. Zahlungen an öffentliche Einrichtungen oder solche, durch die der Bund selbst eine Leistung erbringt, werden als öffentliche Leistungen gewertet. Bei Ausgaben, die mehrere Empfängergruppen begünstigen, richtet sich die Zuteilung nach der größten Empfängergruppe. Separat umfasst der Datensatz außerdem die Ausgaben in ausgewählten Politikbereichen (z. B. KTF). Aufgrund von Unregelmäßigkeiten im Layout der gedruckten Bundeshaushalte kann es bei der automatisierten Erfassung zu Auslassungen von Haushaltstiteln kommen, die händisch korrigiert werden müssen. Dazu kann es durch Unterschiede in der Methodik zu kleinen Abweichungen von den vom Bund ausgewiesenen (und angepassten) Gesamtausgaben kommen. Diese belaufen sich auf maximal 0,8 Prozent des jährlichen Ausgabenvolumens. Wir arbeiten kontinuierlich an der Verbesserung der Datenqualität. Unter Angabe der korrekten Quellenangabe können Nutzer diese Daten frei nutzen, transformieren und verbreiten. Mithilfe der Daten vertretene Positionen geben die Position der Nutzer wieder, nicht aber die des Dezernat Zukunft.',
    contact: 'Ansprechpartner:',
  },

  chart: {
    noTitleData: 'Keine Titel-Daten verfügbar',
    noDataForFilters: 'Keine Daten für die aktuellen Filter',
    noDataForYear: (year: number) => `Keine Daten für ${year} verfügbar`,
    // Subtitles are a description plus an optional click hint; a locked embed
    // drops the hint (see withHint in the charts).
    hintSeparator: ' – ',
    hintDrill: 'Klicken zum Aufschlüsseln',
    hintFilterTable: 'Klicken zum Filtern der Tabelle',
    hintTitlesPick: 'Titel anklicken zum Filtern, Sonstiges für weitere Titel',
    areaSingleTitle: 'Soll- und Ist-Werte im Vergleich',
    areaRoot: 'Ist-Werte (schattiert: Soll-Werte für Jahre ohne Ist-Daten)',
    topTitles: 'Top 5 Titel + Sonstiges',
    subTypes: 'Unterkategorien',
    barSingleTitle: 'Einzelner Titel ausgewählt',
    barRootIst: 'Ist-Werte',
    barRootSoll: 'Soll-Werte (keine Ist-Daten)',
    barRootRevenue: (year: number) => `Gesamteinnahmen des Bundes ${year} - Jahresansicht`,
    barRootExpenditure: (year: number) => `Gesamtausgaben des Bundes ${year} - Jahresansicht`,
    sollBand: 'Soll-Werte',
    showSonstigesTitles: 'Titel aus Sonstiges anzeigen',
    backToSonstiges: 'Wieder zu Sonstiges',
    backToSonstigesAria: (name: string) => `${name} wieder zu Sonstiges`,
    tooltipIst: 'Ist-Wert',
    tooltipSoll: 'Soll-Wert (keine Ist-Daten)',
    expand: 'Chart ausklappen',
    sollYearsCaption: '* Jahre ohne Ist-Daten zeigen Soll-Werte',
    exportChart: 'Chart exportieren',
    downloadPng: 'Als PNG herunterladen',
    downloadSvg: 'Als SVG herunterladen',
    embedChart: 'Diagramm einbetten',
  },

  tooltip: {
    yearWithType: (year: number, isIst: boolean) => `Jahr: ${year} (${isIst ? 'Ist' : 'Soll'})`,
    total: (value: string) => `Gesamt: ${value}`,
    more: (count: number) => `... und ${count} weitere`,
    sollLabel: 'Soll:',
    istLabel: 'Ist:',
    einzelplanShort: 'EP',
    kapitelShort: 'KP',
  },

  titleDetails: {
    fallbackTitle: 'Titel Details',
    einzelplan: 'Einzelplan:',
    kapitel: 'Kapitel:',
    history: 'Historische Daten',
    year: 'Jahr',
  },

  sonstigesPicker: {
    dialogLabel: 'Titel aus Sonstiges auswählen',
    heading: 'In »Sonstiges« enthalten',
    loading: 'Wird geladen…',
    count: (count: number) => `${count} Titel · größter Anteil zuerst`,
    noMoreTitles: 'Keine weiteren Titel',
    searchPlaceholder: 'Titel suchen…',
    searchAria: 'Titel suchen',
    loadError: 'Titel konnten nicht geladen werden.',
    noMatch: 'Kein Titel gefunden.',
    noMoreInSonstiges: 'Keine weiteren Titel in Sonstiges.',
    colTitle: 'Titel',
    colIst: 'Höchster Ist-Wert',
    colShare: 'Anteil an Sonstiges',
    rowRemove: 'Wieder zu Sonstiges',
    rowAdd: 'Als eigene Fläche anzeigen',
    selected: (count: number) => `${count} ausgewählt`,
    prompt: 'Titel zum Aufschlüsseln wählen',
  },

  filterPanel: {
    title: 'Filter',
    reset: 'zurücksetzen',
    closeSheet: 'Filter schließen',
    politikfeld: 'Politikfelder & Empfänger',
    oberkategorie: 'Oberkategorie',
    allOberkategorien: 'Alle Oberkategorien',
    unterkategorie: 'Unterkategorie',
    allUnterkategorien: 'Alle Unterkategorien',
    haushaltsstelle: 'Haushaltsstelle',
    einzelplan: 'Einzelplan',
    allEinzelplaene: 'Alle Einzelpläne',
    kapitel: 'Kapitel',
    allKapitel: 'Alle Kapitel',
    sondervermoegen: 'Sondervermögen',
    allSondervermoegen: 'Alle Sondervermögen',
    gruppierungSection: 'Gruppierung',
    hauptgruppe: 'Hauptgruppe',
    allHauptgruppen: 'Alle Hauptgruppen',
    obergruppe: 'Obergruppe',
    allObergruppen: 'Alle Obergruppen',
    gruppierung: 'Gruppierung',
    allGruppierungen: 'Alle Gruppierungen',
    funktionSection: 'Funktion',
    hauptfunktion: 'Hauptfunktion',
    allHauptfunktionen: 'Alle Hauptfunktionen',
    oberfunktion: 'Oberfunktion',
    allOberfunktionen: 'Alle Oberfunktionen',
    funktion: 'Funktion',
    allFunktionen: 'Alle Funktionen',
  },

  select: {
    noResultsFor: (term: string) => `Keine Ergebnisse für "${term}"`,
    noResults: 'Keine Ergebnisse',
    resultsCount: (shown: number, total: number) => `${shown} von ${total} Ergebnissen`,
    selectedCount: (count: number) => `${count} ausgewählt`,
  },

  search: {
    placeholder: 'Suche nach Haushaltstitel...',
    einzelplan: (code: string) => `EP ${code}`,
    kapitel: (code: string) => `KP ${code}`,
  },

  chips: {
    keys: {
      query: 'Suche',
      expenditureType: 'Oberkategorie',
      subExpType: 'Unterkategorie',
      einzelplan: 'Einzelplan',
      kapitel: 'Kapitel',
      hauptgruppe: 'Hauptgruppe',
      obergruppe: 'Obergruppe',
      gruppierung: 'Gruppierung',
      hauptfunktion: 'Hauptfunktion',
      oberfunktion: 'Oberfunktion',
      funktion: 'Funktion',
      prefix: 'Präfix',
      kapitelScope: 'Umfang',
      drillDownType: 'Oberkategorie',
      drillDownSubType: 'Unterkategorie',
      selectedTitle: 'Titel',
    },
    query: (value: string) => `„${value}“`,
    resetAllAria: 'Alle Filter zurücksetzen',
    resetAll: '↺ Alle zurücksetzen',
    caption: 'Aktive Filter:',
    removeAria: (key: string) => `Filter ${key} entfernen`,
  },

  filterInfo: {
    prefix: (parts: string) => `Gefiltert nach: ${parts}`,
    einzelplan: 'Einzelplan',
    kapitel: 'Kapitel',
    funktion: 'Funktion',
    oberfunktion: 'Oberfunktion',
    hauptfunktion: 'Hauptfunktion',
    gruppierung: 'Gruppierung',
    obergruppe: 'Obergruppe',
    hauptgruppe: 'Hauptgruppe',
    // Human-readable names for known Titel prefixes; unknown ones fall back
    // to the generic titlePrefix wording.
    prefixLabels: { B: 'Bereichsausnahme' } as Record<string, string>,
    titlePrefix: (prefix: string) => `Titel-Präfix ${prefix}`,
    kernhaushalt: 'Kernhaushalt (ohne Sondervermögen)',
    query: (query: string) => `Suche: "${query}"`,
  },

  table: {
    root: 'Haushaltstitel',
    entries: (count: string) => `${count} Einträge`,
    // Shown only in English, where the UI around them is translated.
    germanTitlesHint: '',
    exportCsv: 'Als CSV exportieren',
    noEntries: 'Keine Einträge gefunden',
    colTitel: 'Titel',
    colZweckbestimmung: 'Zweckbestimmung',
    colPolitikfeld: 'Politikfeld & Empfänger',
    cardPolitikfeld: 'Politikfeld',
    colIst: (year: number) => `Ist (${year})`,
    colSoll: (year: number) => `Soll (${year})`,
    details: 'Details anzeigen',
  },

  pagination: {
    previousAria: 'Vorherige Seite',
    previous: 'Zurück',
    nextAria: 'Nächste Seite',
    next: 'Weiter',
  },

  exportDialog: {
    title: 'Daten exportieren',
    loading: 'Daten werden geladen...',
    building: 'CSV wird erstellt...',
    failed: 'Export fehlgeschlagen',
    current: 'Aktuelle Ansicht',
    currentDescription: (count: string) => `${count} Einträge mit aktuellen Filtern`,
    all: 'Alle Daten',
    allDescription: 'Alle Einträge ohne Filter exportieren',
    cancel: 'Abbrechen',
    export: 'Exportieren',
  },

  embedDialog: {
    title: 'Diagramm einbetten',
    intro:
      'Dieser Code zeigt das Diagramm genau in der aktuellen Ansicht — mit denselben Filtern und derselben Aufschlüsselung. Die Daten bleiben live.',
    height: 'Höhe (px)',
    pinYear: (year: number) => `Jahr ${year} fixieren`,
    unpinnedHint: 'Ohne fixiertes Jahr zeigt die Einbettung immer das aktuellste Haushaltsjahr.',
    resizeHint: (height: number) =>
      `Die zweite Zeile ist optional — sie passt die Höhe automatisch an den Inhalt an. Ohne sie bleibt die Einbettung bei ${height} px.`,
    copied: 'In die Zwischenablage kopiert.',
    copy: 'Code kopieren',
  },

  embed: {
    trackerLink: 'Zum vollständigen Haushaltstracker →',
    unavailableTitle: 'Diese Ansicht ist nicht mehr verfügbar.',
    unavailableBody:
      'Für die eingebettete Auswahl liegen keine Daten vor — möglicherweise hat sich der Haushalt seit der Einbettung geändert.',
  },

  spotlights: {
    title: 'Spotlights',
    // Card texts by spotlight id (components/Spotlights.tsx defines the filters).
    cards: {
      kernhaushalt: {
        label: 'Kernhaushalt',
        title: 'Kernhaushalt',
        description: 'Ohne Sondervermögen.',
      },
      'klima-fonds': {
        label: 'KTF',
        title: 'Klima- und Transformations­fonds',
        description: 'Was die Bundesregierung für den Klimaschutz ausgibt.',
      },
      svik: {
        label: 'SVIK',
        title: 'Sondervermögen Infrastruktur und Klimaneutralität',
        description: '500 Milliarden Euro für Infrastruktur und Klimaschutz in Bund und Ländern.',
      },
      bereichsausnahmen: {
        label: 'Bereichsausnahme',
        title: 'Bereichsausnahme',
        description: 'Ausgaben für Verteidigung und Sicherheit, die nicht unter die Schuldenbremse fallen.',
      },
      investitionen: {
        label: 'Investitionen',
        title: 'Investitionen',
        description: 'Schienen, Straßen, Wohnungsbau und Co.',
      },
      'subventionen-unternehmen': {
        label: 'Subventionen an Unternehmen',
        title: 'Subventionen an Unternehmen',
        description: 'Wie viel Geld Unternehmen vom Bund bekommen.',
      },
      'oeffentliche-leistungen': {
        label: 'Öffentliche Leistungen',
        title: 'Öffentliche Leistungen',
        description: 'Zahlungen an öffentliche Einrichtungen oder Leistungen, die der Bund selbst erbringt.',
      },
      finanzierungskosten: {
        label: 'Finanzierungskosten',
        title: 'Finanzierungskosten',
        description: 'Wie viel der Bund für seine Schulden zahlt.',
      },
      bildung: {
        label: 'Bildung',
        title: 'Bildung',
        description: 'Was der Bund für Bildung ausgibt.',
      },
    },
  },

  csv: {
    einzelplan: 'Einzelplan',
    kapitel: 'Kapitel',
    type: 'E/A',
    titelFunktion: 'Titel Funktion',
    zweckbestimmung: 'Zweckbestimmung',
    kategorie: 'Kategorie',
    oberkategorie: 'Oberkategorie',
    unterkategorie: 'Unterkategorie',
    ist: 'Ist',
    soll: 'Soll',
    positionChange: 'wechsel_haushaltsstelle',
    exportFilenamePrefix: 'haushalt_export',
    chartFilenamePrefix: 'haushalt_chart',
  },
};

export type Messages = typeof de;
